import clsx from "clsx";
import React, { Component } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";

import { isNone } from "~/common/assert";
import { PayerId, PAYERS_TRANSLATION_MAP, PAYMENT_METHODS_TRANSLATION_MAP, PAYMENT_OPTIONS_TRANSLATION_MAP, PaymentMethodId, PaymentOptionId } from "~/common/constants";
import {
  CourseId,
  getCapLikeCourseOptionTag,
  isAdditionalAlignersCourse,
  isExpertCourse,
  isPowerCapCourse,
  isPromoCodeAvailableForCourse,
  isRetainersCourse,
  isTransparentCourse,
} from "~/common/courses";
import { remoteLog } from "~/common/logging";
import { RedesignQuestionTooltip } from "~/common/redesign-v1/components/question-tooltip";
import { FormatNumber } from "~/components/common/FormatNumber";
import { Label } from "~/components/ui/label";
import { LabelledInput } from "~/components/ui/labelled-input";
import { LabelledRadio, LabelledRadioGroup } from "~/components/ui/labelled-radio";
import { isArrayOfNumbers, type TCourseInstallment, type TPaymentOptionMap } from "~/slices/course-installment";
import { addInstructions, removeInstructions, selectInstructions } from "~/slices/instructions";
import { selectAdditionalAlignersFormValues, selectCorrectionFormValues } from "~/slices/redux-form";
import type { RootState } from "~/store";
import type { TPatient } from "~/types/patient";
import type { SetDirtyFn } from "~/types/patient-new";

import { getAdditionalAlignersAmount } from "./patient_new_additional_aligners";
import { getExpertSupportPrice } from "./patient_new_additional_services";

const mapStateToProps = (state: RootState) => {
  return {
    instructions: selectInstructions(state),
    formValues: selectCorrectionFormValues(state),
    additionalAlignersFormValues: selectAdditionalAlignersFormValues(state),
  };
};

const mapDispatchToProps = {
  addInstruction: addInstructions,
  removeInstructions,
};

type PatientNewPaymentProps = PropsFromRedux & {
  setDirty: SetDirtyFn;
  courseInstallment: TCourseInstallment;
};

class PatientNewPayment extends Component<PatientNewPaymentProps> {
  constructor(props: PatientNewPaymentProps) {
    super(props);
    this.selectPayer = this.selectPayer.bind(this);
    this.selectPaymentMethod = this.selectPaymentMethod.bind(this);
    this.selectPaymentOption = this.selectPaymentOption.bind(this);
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "patient_new_payment");
  }

  selectPaymentMethod(value: PaymentMethodId) {
    this.props.addInstruction({ payment_method_id: value });
    this.props.setDirty("payment_method_id", value);
  }

  selectPaymentOption(value: PaymentOptionId) {
    this.props.addInstruction({ payment_option_id: value, sber_credit: false });
    this.props.setDirty("payment_option_id", value);
  }

  changePromoCode(value: string) {
    this.props.addInstruction({ promo_code: value });
    this.props.setDirty("promo_code", value);
  }

  selectPayer(value: PayerId) {
    this.props.addInstruction({ payer_id: value });
    this.props.setDirty("payer_id", value);

    if (value == PayerId.PATIENT) {
      if (this.props.instructions.payment_method_id != PaymentMethodId.CARD) {
        this.props.removeInstructions({ payment_method_id: null });
      }
    } else if (value == PayerId.DOCTOR) {
      if (this.props.instructions.payment_method_id != PaymentMethodId.CARD) {
        this.props.removeInstructions({ payment_method_id: null });
      }
      this.removePatientPayer();
    } else {
      if (this.props.instructions.payment_method_id != PaymentMethodId.INVOICE) {
        this.props.removeInstructions({ payment_method_id: null });
      }
      this.removePatientPayer();
    }
  }

  removePatientPayer() {
    this.props.removeInstructions({
      email: null,
      payer_last_name: null,
      payer_first_name: null,
      payer_patronymic: null,
    });
    if (this.props.instructions.sber_credit) {
      this.props.removeInstructions({ payment_option_id: null });
    }
    this.props.addInstruction({ sber_credit: false });
  }

  render() {
    const {
      instructions: { course_id, payer_id, promo_code = "", additional_service_tags },
      formValues: { arch, quantity } = {},
      additionalAlignersFormValues: { aligners = [] } = {},
      courseInstallment,
    } = this.props;

    const installments = courseInstallment[course_id as CourseId] ?? {};
    const courseOptionTag = getCapLikeCourseOptionTag(course_id, arch, quantity);
    const additionalAlignersAmount = getAdditionalAlignersAmount(aligners);

    return (
      <div>
        <h3 id="payment-block-label" className="block" style={{ fontWeight: "900" }}>
          <FormattedMessage id="patient.form.payment.title" />
        </h3>

        <PayerRadioGroup onValueChange={(value) => this.selectPayer(value)} />

        <PaymentMethodRadioGroup payer_id={payer_id} onValueChange={(value) => this.selectPaymentMethod(value)} />

        <PaymentOptionRadioGroup
          installments={installments}
          course_id={course_id}
          course_option_tag={courseOptionTag}
          isSberCredit={false}
          additionalAlignersAmount={additionalAlignersAmount}
          additionalServicesAmount={getExpertSupportPrice(additional_service_tags)}
          onValueChange={(value) => this.selectPaymentOption(value)}
        />

        {isPromoCodeAvailableForCourse(course_id) ? <PromoCodeInput value={promo_code} onValueChange={(value) => this.changePromoCode(value)} /> : null}
      </div>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(PatientNewPayment);

export function PayerRadioGroup({ payer_id, onValueChange }: { payer_id?: PayerId; onValueChange(value: PayerId): void }) {
  const payers = [PayerId.CLINIC, PayerId.DOCTOR, PayerId.PATIENT];

  return (
    <LabelledRadioGroup className="form-group" label={<FormattedMessage id="HEADER_PAYER" />} labelProps={{ id: "validation-payer_id" }} required>
      {payers.map((payer) => (
        <LabelledRadio
          key={payer}
          label={<FormattedMessage id={PAYERS_TRANSLATION_MAP[payer]} />}
          name="payer_id"
          defaultValue={payer}
          checked={payer_id ? payer_id == payer : undefined}
          onChange={() => onValueChange(payer)}
        />
      ))}
    </LabelledRadioGroup>
  );
}

export function PaymentMethodRadioGroup({
  payer_id,
  payment_method_id,
  onValueChange,
}: {
  payer_id: PayerId | undefined;
  payment_method_id?: PaymentMethodId | null;
  onValueChange(value: PaymentMethodId): void;
}) {
  const paymentMethods = [PaymentMethodId.INVOICE, PaymentMethodId.CARD];

  const payerToPaymentMethod: Record<PayerId, PaymentMethodId> = {
    [PayerId.CLINIC]: PaymentMethodId.INVOICE,
    [PayerId.DOCTOR]: PaymentMethodId.CARD,
    [PayerId.PATIENT]: PaymentMethodId.CARD,
  };

  return (
    <LabelledRadioGroup className="form-group" label={<FormattedMessage id="HEADER_PAYMENT_METHOD" />} labelProps={{ id: "validation-payment_method_id" }} required>
      {paymentMethods.map((paymentMethod) => {
        if (payer_id != null && paymentMethod != payerToPaymentMethod[payer_id]) {
          return null;
        }

        return (
          <LabelledRadio
            key={paymentMethod}
            label={<FormattedMessage id={PAYMENT_METHODS_TRANSLATION_MAP[paymentMethod]} />}
            name="payment_method_id"
            defaultValue={paymentMethod}
            checked={payment_method_id ? payment_method_id == paymentMethod : undefined}
            onChange={() => onValueChange(paymentMethod)}
          />
        );
      })}
    </LabelledRadioGroup>
  );
}

export function PaymentOptionRadioGroup({
  payment_option_id,
  installments,
  isSberCredit,
  course_id,
  course_option_tag,
  additionalAlignersAmount,
  additionalServicesAmount = 0,
  onValueChange,
}: {
  payment_option_id?: PaymentOptionId | null;
  installments: Partial<TPaymentOptionMap>;
  isSberCredit: boolean;
  course_id?: TPatient["course"]["course_id"] | number;
  course_option_tag?: TPatient["course"]["course_option_tag"];
  additionalAlignersAmount: number;
  additionalServicesAmount?: number;
  onValueChange(value: PaymentOptionId): void;
}) {
  const isExclusiveCourse = installments.exclusive === true;
  const isExpert = isExpertCourse(course_id);
  const isTransparent = isTransparentCourse(course_id);

  const basePaymentOptions = [PaymentOptionId.ADVANCED, PaymentOptionId.SPLIT_2_2, PaymentOptionId.SPLIT_2, PaymentOptionId.SPLIT_3_2, PaymentOptionId.SPLIT_3];

  const paymentOptions = isExclusiveCourse ? (isExpert ? [PaymentOptionId.ADVANCED, PaymentOptionId.SPLIT_2] : [PaymentOptionId.SPLIT_2_2]) : basePaymentOptions;

  const advancedPaymentOptions = [PaymentOptionId.SPLIT_2_2, PaymentOptionId.SPLIT_3_2];

  const paymentOptionToMaxCount: Record<PaymentOptionId, number> = {
    [PaymentOptionId.ADVANCED]: 1,
    [PaymentOptionId.SPLIT_2]: 2,
    [PaymentOptionId.SPLIT_2_2]: 2,
    [PaymentOptionId.SPLIT_3]: 3,
    [PaymentOptionId.SPLIT_3_2]: 3,
  };

  const hasSplit3Installment = PaymentOptionId.SPLIT_3 in installments || PaymentOptionId.SPLIT_3_2 in installments;

  return (
    <div className="form-group">
      <Label id="validation-payment_option_id" required readOnly>
        <FormattedMessage id="HEADER_PAYMENT_OPTION" />
      </Label>

      <div className="radio-list tw-m-0 tw-mt-2 tw-flex tw-flex-col tw-gap-1 tw-overflow-hidden">
        {paymentOptions.map((paymentOption) => {
          const isCourseSelected = course_id != undefined && !isNone(course_id);

          if ((isCourseSelected && !(paymentOption in installments)) || (!isCourseSelected && advancedPaymentOptions.includes(paymentOption))) {
            return null;
          }

          const checked = payment_option_id ? payment_option_id == paymentOption && !isSberCredit : undefined;

          let prices = installments[paymentOption] ?? [];

          if (isRetainersCourse(course_id) || isPowerCapCourse(course_id)) {
            if (course_option_tag && !isArrayOfNumbers(prices)) {
              prices = [prices.find((inst) => inst.tag == course_option_tag)?.price ?? NaN];
            } else {
              prices = [];
            }
          }

          if (isAdditionalAlignersCourse(course_id) && isArrayOfNumbers(prices) && prices[0] != undefined) {
            prices = [additionalAlignersAmount > 0 ? prices[0] * additionalAlignersAmount : NaN];
          }

          if (additionalServicesAmount > 0 && isArrayOfNumbers(prices) && prices.length > 0) {
            prices = prices.map((price, index) => (index === prices.length - 1 ? price + additionalServicesAmount : price));
          }

          return (
            <div key={paymentOption} className={clsx("tw-flex tw-min-h-[26px] tw-max-w-fit tw-gap-4 max-sm:tw-flex-col", "sm:tw-items-center")}>
              <label className={clsx("tw-mb-0 tw-inline-flex tw-items-center tw-gap-2", hasSplit3Installment ? "tw-min-w-[270px]" : "tw-min-w-[170px]")}>
                <span className="rd-radio">
                  <input
                    type="radio"
                    aria-labelledby={clsx("validation-payment_option_id", `payment-option-value-${paymentOption}`)}
                    id={`payment-option-value-${paymentOption}`}
                    className="rd-radio__input"
                    name="payment_option_id"
                    value={paymentOption}
                    checked={checked}
                    onChange={() => onValueChange(paymentOption)}
                  />
                  <span className="rd-radio__face" aria-hidden="true">
                    <span className="rd-radio__dot" />
                  </span>
                </span>

                <FormattedMessage
                  id={
                    (paymentOption == PaymentOptionId.SPLIT_3 || paymentOption == PaymentOptionId.SPLIT_3_2) &&
                    (course_id == CourseId.LIMITLESS ||
                      (course_option_tag && (course_option_tag == "LIMITLESS_3_STEPS_V1" || course_option_tag == "LIMITLESS_V2" || course_option_tag == "LIMITLESS_3_STEPS_V2")))
                      ? "PO_SPLIT_3_NO_OVERPAY"
                      : PAYMENT_OPTIONS_TRANSLATION_MAP[paymentOption]
                  }
                />
              </label>

              {isCourseSelected && isArrayOfNumbers(prices) ? (
                <PricesTimeline
                  maxCount={paymentOptionToMaxCount[paymentOption]}
                  prices={prices}
                  showCapsLabelForZero={isExclusiveCourse && isTransparent}
                  showPowerCapPriceHint={isPowerCapCourse(course_id)}
                />
              ) : null}
            </div>
          );
        })}
      </div>

      <div id="form_payment_option_error"> </div>
    </div>
  );
}

function PricesTimeline({
  prices,
  maxCount,
  showCapsLabelForZero = false,
  showPowerCapPriceHint = false,
}: {
  prices: number[];
  maxCount: number;
  showCapsLabelForZero?: boolean;
  /** Course is power cap and no arch is picked yet, so price is unknown — explain why on the "?" placeholder. */
  showPowerCapPriceHint?: boolean;
}) {
  return (
    <div className="tw-relative tw-hidden tw-max-w-xs tw-items-center tw-gap-14 sm:tw-gap-16 desktop:tw-flex">
      {maxCount > 1 ? <div className="tw-absolute tw-inset-x-0 tw-h-0.5 tw-bg-[#f4f4f5]" /> : null}

      {Array.from({ length: maxCount }, (_, i) => {
        const price = prices[i];
        const showCapsLabel = showCapsLabelForZero && (price === 0 || price == null);

        return (
          <div
            key={i}
            className={clsx(
              "tw-rounded-3xl tw-bg-[#E9ECF3] tw-px-3 tw-py-1 tw-font-semibold tw-text-brand-black",
              "tw-z-10 tw-flex tw-items-center tw-justify-center",
              "tw-min-h-[26px] tw-min-w-[69px] tw-self-start tw-text-xs",
            )}
          >
            {showCapsLabel ? (
              <FormattedMessage id="payment.caps_label" />
            ) : price != null ? (
              <FormatNumber value={price} />
            ) : showPowerCapPriceHint ? (
              <RedesignQuestionTooltip trigger="?" testId="power-cap-price-hint-tooltip" contentClassName="tw-max-w-[320px]">
                Для расчёта стоимости выберите зубные дуги, на которые планируется изготовление силовых кап.
              </RedesignQuestionTooltip>
            ) : (
              "?"
            )}
          </div>
        );
      })}
    </div>
  );
}

export function PromoCodeInput({ value, onValueChange }: { value: string; onValueChange(value: string): void }) {
  const intl = useIntl();

  return (
    <div className="row">
      <div className="col-md-6">
        <LabelledInput
          label={<FormattedMessage id="HEADER_PROMO_CODE" />}
          placeholder={intl.formatMessage({ id: "HEADER_PROMO_CODE_PLACEHOLDER" })}
          id="promo-code"
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
        />
      </div>
    </div>
  );
}
